"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WalmartClient = exports.ApiAuthType = void 0;
const base_client_1 = require("@deliverr/base-client");
const WalmartError_1 = require("./WalmartError");
const client_auth_1 = require("@deliverr/client-auth");
const core_auth_1 = require("@deliverr/core-auth");
var ApiAuthType;
(function (ApiAuthType) {
    ApiAuthType["SIGNATURE"] = "SIGNATURE";
    ApiAuthType["TOKEN"] = "TOKEN";
})(ApiAuthType = exports.ApiAuthType || (exports.ApiAuthType = {}));
class WalmartClient extends base_client_1.ApiBasicAuthClient {
    constructor(config = {}) {
        const { authEngine } = config;
        const clientConfig = Object.assign(Object.assign({}, config), { baseURL: `${process.env.WALMART_SERVICE_URL}/v1`, authEngine });
        if (authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            clientConfig.tokenRefreshFn = new client_auth_1.OAuthClientTokenBuilder(clientConfig).getTokenFn();
        }
        else if (authEngine === core_auth_1.AuthEngine.BASIC) {
            clientConfig.basicAuthUser = config.basicAuthUser || process.env.WALMART_BASIC_AUTH_USER;
            clientConfig.basicAuthPassword = config.basicAuthPassword || process.env.WALMART_BASIC_AUTH_PASSWORD;
        }
        super(clientConfig);
    }
    async submitApiKeys(sellerId, consumerId, privateKey) {
        try {
            await this.put(`/api-key`, {
                sellerId,
                consumerId,
                privateKey,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                WalmartError_1.WalmartError.INVALID_API_KEY,
                WalmartError_1.WalmartError.INVALID_API_PERMISSIONS,
                WalmartError_1.WalmartError.NON_DELIVERR_CREDENTIALS,
            ]);
        }
    }
    async getApiAuthType(sellerId) {
        const apiCredentials = await this.get(`/api-key/${sellerId}`);
        return apiCredentials.privateKey.length > 150 ? ApiAuthType.SIGNATURE : ApiAuthType.TOKEN;
    }
    async getWalmartStatus(sellerId) {
        return this.get(`/status/${sellerId}`);
    }
    async getDskuStateCoverage(dsku) {
        return this.get(`/coverage/${dsku}`);
    }
    async getStateCoverageByServiceLevel(dsku) {
        try {
            return await this.get(`/coverage/servicelevel/${dsku}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async activateSeller(sellerId) {
        return this.put(`/api-key/${sellerId}/activate`, {});
    }
    async deactivateSeller(sellerId) {
        return this.put(`/api-key/${sellerId}/deactivate`, {});
    }
    async getOrder(sellerId, walmartOrderId) {
        try {
            return await this.get(`/seller/${sellerId}/order/${walmartOrderId}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                WalmartError_1.WalmartError.API_KEYS_NOT_FOUND,
                WalmartError_1.WalmartError.INVALID_API_KEY,
                WalmartError_1.WalmartError.INVALID_API_PERMISSIONS,
                WalmartError_1.WalmartError.INVALID_ORDER_ID,
                WalmartError_1.WalmartError.ORDER_NOT_FOUND,
            ]);
        }
    }
    async getOrderDeliveryDetails(sellerId, walmartOrderId, orderCreationTime, dskus, destinationZip) {
        try {
            const { promisedDeliveryTime: promisedDeliveryTimeStr, fastTagStatus, deliveryInDays, } = await this.get(`/seller/${sellerId}/order/${walmartOrderId}/delivery`, {
                dskus,
                orderCreationTime,
                destinationZip,
            });
            const promisedDeliveryTime = promisedDeliveryTimeStr ? new Date(promisedDeliveryTimeStr) : undefined;
            return { promisedDeliveryTime, fastTagStatus, deliveryInDays };
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getOrders(sellerId, createdStartDate, pageSize, nextCursor, createdEndDate) {
        return this.get(`/seller/${sellerId}/orders`, {
            createdStartDate,
            pageSize,
            nextCursor,
            createdEndDate,
        });
    }
    async getListings(sellerId, pageSize, nextCursor) {
        return this.get(`/seller/${sellerId}/listings`, {
            pageSize,
            nextCursor,
        });
    }
    async getAllActiveSellers(activatedSince, limit, offset) {
        return this.get("/sellers", {
            activatedSince: activatedSince && activatedSince.toISOString(),
            limit,
            offset,
        });
    }
    async getNationwideCoverageThreshold(serviceLevel = 2) {
        return this.get(`/coverage/servicelevel/${serviceLevel}/threshold`);
    }
    async getLinkForDsku(sellerId, dsku) {
        try {
            return await this.get(`/seller/${sellerId}/item/${dsku}/link`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                WalmartError_1.WalmartError.INVALID_API_KEY,
                WalmartError_1.WalmartError.NOT_FOUND,
                WalmartError_1.WalmartError.NOT_FOUND_IN_MARKETPLACE,
            ]);
        }
    }
    async isFastTagEnabled(sellerId, dsku) {
        try {
            return await this.get(`/seller/${sellerId}/item/${dsku}/active`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [WalmartError_1.WalmartError.NOT_FOUND]);
        }
    }
    async enableFastTag(sellerId, dsku) {
        try {
            await this.put(`/seller/${sellerId}/item/${dsku}`, { isTwoDayEnabled: true });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [WalmartError_1.WalmartError.NOT_FOUND]);
        }
    }
    async disableFastTag(sellerId, dsku) {
        try {
            await this.put(`/seller/${sellerId}/item/${dsku}`, { isTwoDayEnabled: false });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [WalmartError_1.WalmartError.NOT_FOUND]);
        }
    }
    async updateLinkForDsku(sellerId, dsku, listingUrl, productIdentifier) {
        try {
            return await this.put(`/seller/${sellerId}/item/${dsku}/listing`, {
                listingUrl,
                msku: productIdentifier,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                WalmartError_1.WalmartError.INVALID_URL,
                WalmartError_1.WalmartError.LISTING_IS_MISSING_SKU,
                WalmartError_1.WalmartError.MSKU_NOT_FOUND,
                WalmartError_1.WalmartError.MULTIPLE_VARIANTS,
            ]);
        }
    }
    async mapOrderDskus(sellerId, walmartOrderId, orderItems) {
        try {
            return await this.put(`/seller/${sellerId}/order/${walmartOrderId}/items`, orderItems);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getUnmappedDskus(sellerId) {
        try {
            return await this.get(`/seller/${sellerId}/unmapped-dskus`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async refreshSeller(sellerId) {
        try {
            await this.post(`/walmart/refresh/${sellerId}`, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                WalmartError_1.WalmartError.INVALID_API_KEY,
                WalmartError_1.WalmartError.NOT_FOUND,
                WalmartError_1.WalmartError.NOT_FOUND_IN_MARKETPLACE,
                WalmartError_1.WalmartError.API_KEYS_NOT_FOUND,
            ]);
        }
    }
    async getSellersToRefresh(excludeRefreshedSince, limit, offset) {
        return this.get("/sellers/refresh", {
            excludeRefreshedSince: excludeRefreshedSince.toISOString(),
            limit,
            offset,
        });
    }
}
exports.WalmartClient = WalmartClient;
exports.default = WalmartClient;
//# sourceMappingURL=WalmartClient.js.map